import { useEffect, useState } from "react";
import Template from "../app/Template";
import Header from "../layout/Header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useSearchParams } from "react-router-dom";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";
import Container from "../layout/Container";
import NoContent from "../layout/NoContent";
import Card from "../layout/Card";
import Filters from "../layout/Filters";
import {
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  ExclamationCircleIcon,
  QuestionMarkCircleIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import Checkbox from "../forms/Checkbox";
import Button from "../layout/Button";
import getButtonStyle from "../../utils/styles";
import { addAlert } from "../../slices/alert";
import { generateAlertText } from "../../utils/boilerplate";
import { getColour } from "../../utils/colours";
import Badge from "../layout/Badge";
import ActionButtons from "../layout/ActionButtons";
import { translateText } from "../../utils/translation";
import Table from "../layout/Table";
import { getRooms } from "../../slices/rooms";
import { Cog8ToothIcon, PlusCircleIcon } from "@heroicons/react/24/outline";
import Modal from "../layout/Modal";
import { Dialog } from "@headlessui/react";
import SwitchToggle from "../forms/SwitchToggle";
import {
  SurveyState,
  createSurvey,
  deleteSurveys,
  getSurveyResponses,
  getSurveys,
  updateSurvey,
} from "../../slices/surveys";
import Dropdown from "../forms/Dropdown";
import HorizontalRadio from "../forms/HorizontalRadio";
import SelectOrUnselectAll from "../forms/SelectOrUnselectAll";
import Indicator from "../layout/Indicator";
import { checkArraysContainSameElements, onlyUnique } from "../../utils/arrays";
import DangerZone from "../forms/DangerZone";
import DonutChart from "../charts/radial/DonutChart";
import TextInputMultiLanguage from "../forms/TextInputMultiLanguage";

export default function Surveys() {
  const dispatch = useAppDispatch();

  // Get query params
  const [searchParams] = useSearchParams();
  const searchScored = searchParams.get("scored");
  const dateRangeTerm = searchParams.get("dateRange");

  // Fetch data from server
  useEffect(() => {
    dispatch(getSurveys({ scored: searchScored, dateRange: dateRangeTerm }));
    dispatch(getRooms());
  }, [dispatch, dateRangeTerm, searchScored]);

  // Set state
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [scored, setScored] = useState({ value: "" });
  const [selectedSurvey, setSelectedSurvey] = useState<string | null>(null);
  const [selectedEditSurvey, setSelectedEditSurvey] = useState<{
    id: string;
    name: { [key: string]: string };
    type: string;
    scored: boolean;
    room_target: string;
    rooms: string[];
    status: string;
  } | null>(null);
  const [showEditSurveyModal, setShowEditSurveyModal] =
    useState<boolean>(false);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [createSurveyFormData, setCreateSurveyFormData] = useState<{
    name: { [key: string]: string };
    type: string;
    scored: boolean;
    room_target: string;
    rooms: string[];
  }>({
    name: { EN: "", FR: "", IT: "", DE: "" },
    type: "M",
    scored: false,
    room_target: "A",
    rooms: [],
  });
  const [createQuestionsFormData, setCreateQuestionsFormData] = useState<
    Array<{
      id: number;
      text: { [key: string]: string };
      answers: { [key: number]: { [key: string]: string } };
      correct_answer: any | null;
    }>
  >([
    {
      id: 0,
      text: { EN: "", FR: "", IT: "", DE: "" },
      answers: {
        1: { EN: "", FR: "", IT: "", DE: "" },
        2: { EN: "", FR: "", IT: "", DE: "" },
        3: { EN: "", FR: "", IT: "", DE: "" },
        4: { EN: "", FR: "", IT: "", DE: "" },
      },
      correct_answer: null,
    },
  ]);
  const [updateQuestionsFormData, setUpdateQuestionsFormData] = useState<
    Array<{
      id: number;
      text: { [key: string]: string };
      answers: { [key: number]: { [key: string]: string } };
      correct_answer: any | null;
    }>
  >([]);

  // Extract data from Redux store
  const { user } = useAppSelector((state) => state.auth);
  const { surveys, options } = useAppSelector((state) => state.surveys);
  const { rooms } = useAppSelector((state) => state.rooms);
  const language = user === null ? "UK" : user.language;

  function resetSelectedSurvey(e: any) {
    e.preventDefault();
    setSelectedSurvey(null);
  }

  function selectSurveyToEdit(e: any, args: any[]) {
    e.preventDefault();
    if (surveys.data === null) return;
    const [surveyId] = args;
    const surveyData = surveys.data.filter((svy) => svy.id === surveyId)[0];
    const allAvailableRooms =
      rooms.data === null ? [] : rooms.data.map((x) => x.id);
    setSelectedEditSurvey({
      id: surveyId,
      name: surveyData.name,
      type: surveyData.type[0],
      scored: surveyData.scored,
      room_target: checkArraysContainSameElements(
        surveyData.rooms,
        allAvailableRooms,
      )
        ? "A"
        : "S",
      rooms: surveyData.rooms,
      status: surveyData.status[0],
    });
    const formattedQuestions = surveyData.questions.map(
      (q: any, idx: number) => {
        const formattedAnswers: any = {};
        let correctAnswer: any = null;
        Object.keys(q.answers).forEach((key: any) => {
          formattedAnswers[parseInt(key, 10) + 1] = q.answers[key].text;
          if (q.answers[key].id === q.correct_answer) {
            correctAnswer = parseInt(key, 10) + 1;
          }
        });
        return {
          id: idx,
          text: q.text,
          answers: formattedAnswers,
          correct_answer: correctAnswer,
        };
      },
    );
    setUpdateQuestionsFormData(formattedQuestions);
    setShowEditSurveyModal(true);
  }

  function openCreateSurveyModal(e: any) {
    e.preventDefault();
    setShowCreateModal(true);
  }

  async function confirmCreateSurvey(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    const {
      name,
      type,
      scored,
      room_target: roomTarget,
      rooms: chosenRooms,
    } = createSurveyFormData;

    // Validate top level text fields
    if (name[language] === "") {
      dispatch(
        addAlert(generateAlertText("missing_name", language), "danger", 5000),
      );
      return;
    }

    // Validate rooms
    let selectedRooms = chosenRooms;
    if (roomTarget === "A") {
      selectedRooms = rooms.data === null ? [] : rooms.data.map((x) => x.id);
    }
    if (selectedRooms.length === 0) {
      dispatch(
        addAlert(generateAlertText("missing_rooms", language), "danger", 5000),
      );
      return;
    }

    // Validate and extract questions data
    const formattedQuestions: any[] = [];
    let check = false;
    createQuestionsFormData.forEach((q, idx) => {
      if (q.text[language] === "") {
        dispatch(
          addAlert(
            generateAlertText(
              "survey_multiple_choice_missing_question",
              language,
            ),
            "danger",
            5000,
          ),
        );
        check = true;
      }

      // If scored, validate answer was given
      if (scored) {
        if (q.correct_answer === null) {
          dispatch(
            addAlert(
              generateAlertText(
                "survey_multiple_choice_missing_answer",
                language,
              ),
              "danger",
              5000,
            ),
          );
          check = true;
        }
      }

      // Check number of valid answers > 2
      const validAnswers = Object.values(q.answers).filter(
        (ans) => ans[language].length > 0,
      );
      const validUnique = validAnswers.filter(onlyUnique);
      if (validUnique.length < 2) {
        dispatch(
          addAlert(
            generateAlertText(
              "survey_multiple_choice_missing_answers",
              language,
            ),
            "danger",
            5000,
          ),
        );
        check = true;
      }

      // Check if correct answer matches id of an answer
      const validAnswerKeys = Object.keys(q.answers).filter((a: any) =>
        validUnique.includes(q.answers[a]),
      );
      if (q.correct_answer !== null) {
        if (!validAnswerKeys.includes(q.correct_answer.toString()) && scored) {
          dispatch(
            addAlert(
              generateAlertText(
                "survey_multiple_choice_incorrect_answer",
                language,
              ),
              "warning",
              5000,
            ),
          );
          check = true;
        }
      }

      const validAnswersObj: any = {};
      validAnswerKeys.forEach((k: any, kIdx: any) => {
        const keyVal = `${kIdx + 1}`;
        validAnswersObj[keyVal] = q.answers[k];
      });
      let correctAnswer = null;
      if (scored && q.correct_answer !== null) {
        const originalAnswer = q.answers[q.correct_answer];
        const correctAnswerIdx = validUnique.indexOf(originalAnswer) + 1;
        correctAnswer = correctAnswerIdx.toString();
      }

      const formattedObj = {
        order: idx + 1,
        text: q.text,
        answers: validAnswersObj,
        correct_answer: correctAnswer,
        status: "A",
      };
      formattedQuestions.push(formattedObj);
    });
    if (check) return;

    // Validate and build survey data
    const survey = {
      name,
      type,
      property: user.property.id,
      scored,
      rooms: selectedRooms,
      status: "A",
    };

    console.log({ survey, questions: formattedQuestions });
    setShowCreateModal(false);
    const res: any = await dispatch(
      createSurvey({ survey, questions: formattedQuestions }),
    );
    if (res.payload.status === 201) {
      dispatch(
        addAlert(generateAlertText("create_survey", language), "success", 3000),
      );
      setCreateSurveyFormData({
        name: { EN: "", FR: "", IT: "", DE: "" },
        type: "M",
        scored: false,
        room_target: "A",
        rooms: [],
      });
    } else {
      dispatch(
        addAlert(generateAlertText("create_error", language), "danger", 3000),
      );
      setShowCreateModal(true);
    }
  }

  async function confirmEditSurvey(e: any) {
    e.preventDefault();
    if (selectedEditSurvey === null) return;
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    const {
      id,
      name,
      type,
      scored,
      room_target: roomTarget,
      rooms: chosenRooms,
      status,
    } = selectedEditSurvey;

    // Validate top level text fields
    if (name[language] === "") {
      dispatch(
        addAlert(generateAlertText("missing_name", language), "danger", 5000),
      );
      return;
    }

    // Validate rooms
    let selectedRooms = chosenRooms;
    if (roomTarget === "A") {
      selectedRooms = rooms.data === null ? [] : rooms.data.map((x) => x.id);
    }
    if (selectedRooms.length === 0) {
      dispatch(
        addAlert(generateAlertText("missing_rooms", language), "danger", 5000),
      );
      return;
    }

    // Validate and extract questions data
    const formattedQuestions: any[] = [];
    let check = false;
    updateQuestionsFormData.forEach((q, idx) => {
      if (q.text[language] === "") {
        dispatch(
          addAlert(
            generateAlertText(
              "survey_multiple_choice_missing_question",
              language,
            ),
            "danger",
            5000,
          ),
        );
        check = true;
      }

      // If scored, validate answer was given
      if (scored) {
        if (q.correct_answer === null) {
          dispatch(
            addAlert(
              generateAlertText(
                "survey_multiple_choice_missing_answer",
                language,
              ),
              "danger",
              5000,
            ),
          );
          check = true;
        }
      }

      // Check number of valid answers > 2
      const validAnswers = Object.values(q.answers).filter(
        (ans) => ans[language].length > 0,
      );
      const validUnique = validAnswers.filter(onlyUnique);
      if (validUnique.length < 2) {
        dispatch(
          addAlert(
            generateAlertText(
              "survey_multiple_choice_missing_answers",
              language,
            ),
            "danger",
            5000,
          ),
        );
        check = true;
      }

      // Check if correct answer matches id of an answer
      const validAnswerKeys = Object.keys(q.answers).filter((a: any) =>
        validUnique.includes(q.answers[a]),
      );
      if (q.correct_answer !== null) {
        if (!validAnswerKeys.includes(q.correct_answer.toString()) && scored) {
          dispatch(
            addAlert(
              generateAlertText(
                "survey_multiple_choice_incorrect_answer",
                language,
              ),
              "warning",
              5000,
            ),
          );
          check = true;
        }
      }

      const validAnswersObj: any = {};
      validAnswerKeys.forEach((k: any, kIdx: any) => {
        const keyVal = `${kIdx + 1}`;
        validAnswersObj[keyVal] = q.answers[k];
      });
      let correctAnswer = null;
      if (scored && q.correct_answer !== null) {
        const originalAnswer = q.answers[q.correct_answer];
        const correctAnswerIdx = validUnique.indexOf(originalAnswer) + 1;
        correctAnswer = correctAnswerIdx.toString();
      }

      const formattedObj = {
        order: idx + 1,
        text: q.text,
        answers: validAnswersObj,
        correct_answer: correctAnswer,
        status: "A",
      };
      formattedQuestions.push(formattedObj);
    });
    if (check) return;

    // Validate and build survey data
    const survey = {
      id,
      name,
      type,
      property: user.property.id,
      scored,
      rooms: selectedRooms,
      status,
    };

    setShowEditSurveyModal(false);
    const res: any = await dispatch(
      updateSurvey({ survey, questions: formattedQuestions }),
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(generateAlertText("update_survey", language), "success", 3000),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("update_error", language), "danger", 3000),
      );
      setShowEditSurveyModal(true);
    }
  }

  function selectOrUnselectAllCreateRooms(e: any, args: any[]) {
    e.preventDefault();
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableRooms =
        rooms.data === null ? [] : rooms.data.map((x) => x.id);
      setCreateSurveyFormData({
        ...createSurveyFormData,
        rooms: allAvailableRooms,
      });
    } else {
      setCreateSurveyFormData({ ...createSurveyFormData, rooms: [] });
    }
  }

  function updateRoomForCreateSurvey(roomId: string) {
    if (createSurveyFormData.rooms.includes(roomId)) {
      const updatedRooms = createSurveyFormData.rooms.filter(
        (x) => x !== roomId,
      );
      setCreateSurveyFormData({ ...createSurveyFormData, rooms: updatedRooms });
    } else {
      const updatedRooms = [...createSurveyFormData.rooms, roomId];
      setCreateSurveyFormData({ ...createSurveyFormData, rooms: updatedRooms });
    }
  }

  function selectOrUnselectAllUpdateRooms(e: any, args: any[]) {
    e.preventDefault();
    if (selectedEditSurvey === null) return;
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableRooms =
        rooms.data === null ? [] : rooms.data.map((x) => x.id);
      setSelectedEditSurvey({
        ...selectedEditSurvey,
        rooms: allAvailableRooms,
      });
    } else {
      setSelectedEditSurvey({ ...selectedEditSurvey, rooms: [] });
    }
  }

  function updateRoomForUpdateSurvey(roomId: string) {
    if (selectedEditSurvey === null) return;
    if (selectedEditSurvey.rooms.includes(roomId)) {
      const updatedRooms = selectedEditSurvey.rooms.filter((x) => x !== roomId);
      setSelectedEditSurvey({ ...selectedEditSurvey, rooms: updatedRooms });
    } else {
      const updatedRooms = [...selectedEditSurvey.rooms, roomId];
      setSelectedEditSurvey({ ...selectedEditSurvey, rooms: updatedRooms });
    }
  }

  function addOrRemoveQuestionFromSurveyCreate(e: any, args: any[]) {
    e.preventDefault();
    const [action, questionId] = args;
    if (action === "add") {
      const currentQuestions = [...createQuestionsFormData];
      const newId = currentQuestions.length;
      currentQuestions.push({
        id: newId,
        text: { EN: "", FR: "", IT: "", DE: "" },
        answers: {
          1: { EN: "", FR: "", IT: "", DE: "" },
          2: { EN: "", FR: "", IT: "", DE: "" },
          3: { EN: "", FR: "", IT: "", DE: "" },
          4: { EN: "", FR: "", IT: "", DE: "" },
        },
        correct_answer: null,
      });
      setCreateQuestionsFormData(currentQuestions);
    } else if (action === "remove") {
      const currentQuestions = [...createQuestionsFormData];
      let newQuestions = currentQuestions.filter((q) => q.id !== questionId);
      newQuestions = newQuestions.map((q, index) => ({ ...q, id: index }));
      setCreateQuestionsFormData(newQuestions);
    }
  }

  function addOrRemoveQuestionFromSurveyUpdate(e: any, args: any[]) {
    e.preventDefault();
    const [action, questionId] = args;
    if (action === "add") {
      const currentQuestions = [...updateQuestionsFormData];
      const newId = currentQuestions.length;
      currentQuestions.push({
        id: newId,
        text: { EN: "", FR: "", IT: "", DE: "" },
        answers: {
          1: { EN: "", FR: "", IT: "", DE: "" },
          2: { EN: "", FR: "", IT: "", DE: "" },
          3: { EN: "", FR: "", IT: "", DE: "" },
          4: { EN: "", FR: "", IT: "", DE: "" },
        },
        correct_answer: null,
      });
      setUpdateQuestionsFormData(currentQuestions);
    } else if (action === "remove") {
      const currentQuestions = [...updateQuestionsFormData];
      let newQuestions = currentQuestions.filter((q) => q.id !== questionId);
      newQuestions = newQuestions.map((q, index) => ({ ...q, id: index }));
      setUpdateQuestionsFormData(newQuestions);
    }
  }

  async function confirmDeleteSurvey(e: any, args: any[]) {
    e.preventDefault();
    const [surveyIds] = args;
    if (surveyIds.length === 0) return;
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 3000),
      );
      return;
    }
    if (surveyIds === null || surveyIds === undefined) {
      dispatch(
        addAlert(
          generateAlertText("missing_surveys", language),
          "danger",
          3000,
        ),
      );
      return;
    }
    setShowEditSurveyModal(false);
    const res: any = await dispatch(deleteSurveys({ surveyIds: [surveyIds] }));
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("delete_success", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("delete_error", language), "danger", 3000),
      );
      setShowEditSurveyModal(true);
    }
  }

  async function getSelectedSurveyResponses(e: any, surveyId: string) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 3000),
      );
      return;
    }
    setSelectedSurvey(surveyId);
    dispatch(getSurveyResponses({ surveyId }));

    // Scroll to anchor
    const anchor = document.querySelector("#survey-response-anchor");
    if (anchor !== null) anchor.scrollIntoView({ behavior: "smooth" });
  }

  function generateChartData() {
    if (
      selectedSurvey === null ||
      surveys.data === null ||
      surveys.responses.loading ||
      surveys.responses.data === null
    )
      return {
        answerFrequencyData: [],
        roomFrequency: [],
        correctAnswersData: [],
        uniqueAnswers: [],
        activeSurveyResponses: [],
        activeSurveyData: null,
      };

    let activeSurveyResponses = surveys.responses.data[selectedSurvey];
    activeSurveyResponses =
      activeSurveyResponses === null ? [] : activeSurveyResponses;
    const activeSurveyData: SurveyState = surveys.data.filter(
      (x) => x.id === selectedSurvey,
    )[0];

    // Extract questions
    const questions = activeSurveyResponses.map((x) => x.question);
    let uniqueQuestionIds = questions.map((x) => x.id);
    uniqueQuestionIds = uniqueQuestionIds.filter(onlyUnique);
    let uniqueQuestionsText = questions.map((x) => x.text);
    uniqueQuestionsText = uniqueQuestionsText.filter(onlyUnique);

    // Build response frequency data
    const answerFrequencyData = [];
    for (let i = 0; i < uniqueQuestionIds.length; i += 1) {
      const validResponses = activeSurveyResponses.filter(
        (x) => x.question.id === uniqueQuestionIds[i],
      );
      const correctAnswer = activeSurveyData.questions.filter(
        (x: any) => x.id === uniqueQuestionIds[i],
      )[0].correct_answer;
      const numCorrect = validResponses.filter(
        (x) => x.answer.id === correctAnswer,
      ).length;

      const barChartFrequency =
        validResponses.length === 0
          ? null
          : validResponses.map((x) => x.answer.text);
      const uniqueValues =
        barChartFrequency === null ? [] : barChartFrequency.filter(onlyUnique);
      const obj: any = { question: uniqueQuestionsText[i] };
      const qAnswers = [];
      if (barChartFrequency !== null) {
        for (let j = 0; j < uniqueValues.length; j += 1) {
          qAnswers.push({
            id: uniqueValues[j],
            label: uniqueValues[j],
            value: barChartFrequency.filter((x) => x === uniqueValues[j])
              .length,
          });
        }
        obj.answers = qAnswers;
        obj.correct_pct = ((numCorrect / validResponses.length) * 100).toFixed(
          0,
        );
        answerFrequencyData.push(obj);
      }
    }

    // Build room frequency data
    let roomFrequency: any =
      activeSurveyResponses.length === 0
        ? []
        : activeSurveyResponses.map((x) => x.room.name);
    const uniqueRooms = roomFrequency.filter(onlyUnique);
    roomFrequency = uniqueRooms.map((x: any) => ({
      id: x,
      label: x,
      value: roomFrequency.filter((y: any) => y === x).length,
    }));

    return {
      answerFrequencyData,
      roomFrequency,
      activeSurveyResponses,
      activeSurveyData,
    };
  }

  function createQuestionText(e: any, args: any[], selectedLanguage: string) {
    e.preventDefault();
    const existingValues = [...createQuestionsFormData];
    const [index] = args;
    existingValues[index].text[selectedLanguage] = e.target.value;
    setCreateQuestionsFormData(existingValues);
  }

  function getCreateQuestionText(args: any[], selectedLanguage: string) {
    const [index] = args;
    console.log(createQuestionsFormData[index].text);
    return createQuestionsFormData[index].text[selectedLanguage];
  }

  function createAnswerText(e: any, args: any[], selectedLanguage: string) {
    e.preventDefault();
    const existingValues = [...createQuestionsFormData];
    const [index, key] = args;
    existingValues[index].answers[key][selectedLanguage] = e.target.value;
    setCreateQuestionsFormData(existingValues);
  }

  function getCreateAnswerText(args: any[], selectedLanguage: string) {
    const [index, key] = args;
    return createQuestionsFormData[index].answers[key][selectedLanguage];
  }

  function updateQuestionText(e: any, args: any[], selectedLanguage: string) {
    e.preventDefault();
    const updatedValues = [];
    const [index] = args;
    for (let i = 0; i < updateQuestionsFormData.length; i++) {
      if (i === index) {
        updatedValues.push({
          ...updateQuestionsFormData[i],
          text: {
            ...updateQuestionsFormData[i].text,
            [selectedLanguage]: e.target.value,
          },
        });
      } else {
        updatedValues.push(updateQuestionsFormData[i]);
      }
    }
    setUpdateQuestionsFormData(updatedValues);
  }

  function getSelectedQuestionText(args: any[], selectedLanguage: string) {
    const [index] = args;
    return updateQuestionsFormData[index].text[selectedLanguage];
  }

  function updateAnswerText(e: any, args: any[], selectedLanguage: string) {
    e.preventDefault();
    const updatedValues = [];
    const [index, key] = args;
    for (let i = 0; i < updateQuestionsFormData.length; i++) {
      if (i === index) {
        updatedValues.push({
          ...updateQuestionsFormData[i],
          answers: {
            ...updateQuestionsFormData[i].answers,
            [key]: {
              ...updateQuestionsFormData[i].answers[key],
              [selectedLanguage]: e.target.value,
            },
          },
        });
      } else {
        updatedValues.push(updateQuestionsFormData[i]);
      }
    }
    setUpdateQuestionsFormData(updatedValues);
  }

  function getSelectedAnswerText(args: any[], selectedLanguage: string) {
    const [index, key] = args;
    return updateQuestionsFormData[index].answers[key][selectedLanguage];
  }

  const {
    answerFrequencyData,
    roomFrequency,
    activeSurveyResponses,
    activeSurveyData,
  } = generateChartData();

  function generateTableData() {
    if (activeSurveyData == null) return { tableRowData: [], tableColumns: [] };

    function getCorrectOptions(correct: boolean) {
      if (correct && language === "EN") return ["YES", "Yes"];
      if (!correct && language === "EN") return ["NO", "No"];
      if (correct && language === "FR") return ["YES", "Oui"];
      if (!correct && language === "FR") return ["NO", "Non"];
      if (correct && language === "IT") return ["YES", "Sì"];
      if (!correct && language === "IT") return ["NO", "No"];
      return ["YES", "Yes"];
    }

    const tableRowData = activeSurveyResponses.map((x) => ({
      id: x.id,
      datetime: x.datetime_created,
      question: x.question.text,
      room: x.room.name,
      answer: x.answer.text,
      correct: getCorrectOptions(
        x.answer.id ===
          activeSurveyData.questions.filter((y) => y.id === x.question.id)[0]
            .correct_answer,
      ),
    }));

    return { tableRowData };
  }

  const { tableRowData } = generateTableData();

  const surveysData = surveys.data === null ? [] : surveys.data;
  const roomsData = rooms.data === null ? [] : rooms.data;
  const scoredOptions = [
    { value: "true", label: translateText("true", language) },
    { value: "false", label: translateText("false", language) },
  ];
  const surveyTypeChoices =
    options.type === undefined
      ? []
      : Object.keys(options.type).map((key) => [
          key,
          options.type[key][language],
        ]);
  const targetTypes = [
    ["A", translateText("all", language)],
    ["S", translateText("specific", language)],
  ];
  let selectedSurveyResponses: any = [];
  if (surveys.responses.data !== null && selectedSurvey !== null) {
    if (Object.keys(surveys.responses.data).includes(selectedSurvey)) {
      selectedSurveyResponses = surveys.responses.data[selectedSurvey];
    }
  }

  return (
    <Template>
      {/* Header */}
      <Header
        text={translateText("surveys_title", language)}
        actions={
          <ActionButtons
            primaryButton={
              <Button
                type="button"
                className={`${getButtonStyle("solid", "info")}`}
                onClick={openCreateSurveyModal}
              >
                <PlusCircleIcon
                  className={`h-5 w-5 text-white mr-1`}
                  aria-hidden="true"
                />
                {translateText("create_survey", language)}
              </Button>
            }
          />
        }
      />

      {/* Create survey modal */}
      <Modal
        show={showCreateModal}
        setShow={setShowCreateModal}
        confirmText={translateText("create_survey", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmCreateSurvey}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {translateText("create_event", language)}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Name */}
              <TextInputMultiLanguage
                id="name"
                type="text"
                label={translateText("name", language)}
                className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={createSurveyFormData}
                stateName="name"
                onChange={setCreateSurveyFormData}
                defaultLanguage={language}
                languageChoices={
                  surveysData.length === 0
                    ? ["EN", "FR", "IT", "DE"]
                    : Object.keys(surveysData[0].name)
                }
              />

              {/* Survey type */}
              <Dropdown
                id="type"
                className="col-span-full"
                label={translateText("type", language)}
                showLabel={true}
                required={true}
                state={createSurveyFormData}
                stateName="type"
                onChange={setCreateQuestionsFormData}
                defaultValue="M"
              >
                {surveyTypeChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Scored */}
              <SwitchToggle
                id="scored"
                label={translateText("scored_survey", language)}
                showLabel={true}
                description={translateText(
                  "scored_survey_description",
                  language,
                )}
                toggle={createSurveyFormData.scored === true}
                state={createSurveyFormData}
                stateName="scored"
                handler={setCreateSurveyFormData}
              />

              {/* Target */}
              <HorizontalRadio
                label={translateText("target_rooms", language)}
                small={translateText("target_rooms_subtext", language)}
              >
                <>
                  {targetTypes.map((choice) => (
                    <div key={choice[0]} className="flex items-center">
                      <input
                        id={choice[0]}
                        name="build_type"
                        type="radio"
                        defaultChecked={
                          createSurveyFormData.room_target === choice[0]
                        }
                        className="h-4 w-4 border-gray-300 text-primary focus:ring-primary rounded"
                        value={choice[0]}
                        onChange={(e) =>
                          setCreateSurveyFormData({
                            ...createSurveyFormData,
                            room_target: e.target.value,
                          })
                        }
                      />
                      <label
                        htmlFor={choice[0]}
                        className="ml-3 block text-sm font-medium leading-6 text-black"
                      >
                        {choice[1]}
                      </label>
                    </div>
                  ))}
                </>
              </HorizontalRadio>

              {/* Rooms */}
              {createSurveyFormData.room_target === "S" && (
                <>
                  <div>
                    <label className="mb-1 block text-left text-sm font-medium text-black">
                      {translateText("rooms", language)}
                    </label>
                    <p className="text-left text-xs italic text-secondary">
                      {translateText("rooms_template_subtext", language)}
                    </p>
                  </div>

                  {/* Select/unselect all buttons */}
                  <SelectOrUnselectAll
                    handler={selectOrUnselectAllCreateRooms}
                    language={language}
                  />
                  <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                    {roomsData.map((room: any) => (
                      <Checkbox
                        key={room.id}
                        id={room.id}
                        onChange={updateRoomForCreateSurvey}
                        checked={createSurveyFormData.rooms.includes(room.id)}
                        label={room.name}
                      />
                    ))}
                  </div>
                </>
              )}

              {/* Unscored survey build */}
              {createSurveyFormData.scored === false ? (
                <>
                  {/* Questions sections */}
                  {createQuestionsFormData.map((value: any, index: number) => (
                    <div
                      key={`question-${value.id}-${index}`}
                      className="grid grid-cols-1 gap-y-8 border-t border-light pt-4"
                    >
                      <div>
                        {/* Delete question */}
                        {createQuestionsFormData.length > 1 && (
                          <div className="flex justify-start">
                            <Button
                              type="button"
                              className={`${getButtonStyle(
                                "solid",
                                "danger",
                              )} mb-3`}
                              onClick={addOrRemoveQuestionFromSurveyCreate}
                              onClickArgs={["remove", value.id]}
                            >
                              <TrashIcon
                                className={`h-5 w-5 text-white`}
                                aria-hidden="true"
                              />
                            </Button>
                          </div>
                        )}

                        <div className="col-span-full">
                          <div>
                            {/* Question */}
                            <label
                              htmlFor={`header-${index}`}
                              className={`mb-3 block text-left text-sm font-medium text-black`}
                            >
                              {`${translateText("question", language)} ${
                                index + 1
                              }`}
                            </label>
                            <TextInputMultiLanguage
                              id={`question-${index}`}
                              type="text"
                              label=""
                              className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                              placeholder={`${translateText(
                                "add_question_for",
                                language,
                              )} ${translateText("question", language)} ${
                                index + 1
                              }`}
                              required={true}
                              showLabel={false}
                              state={createQuestionsFormData}
                              stateName="question"
                              valueHandler={getCreateQuestionText}
                              valueHandlerArgs={[index]}
                              onChangeHandler={createQuestionText}
                              onChangeHandlerArgs={[index]}
                              defaultLanguage={language}
                              languageChoices={
                                surveysData.length === 0
                                  ? ["EN", "FR", "IT", "DE"]
                                  : Object.keys(surveysData[0].name)
                              }
                            />

                            {/* Answers */}
                            <div className="mt-4 grid grid-cols-1 sm:grid-cols-1 gap-4">
                              {Object.keys(value.answers).map((key: any) => (
                                <TextInputMultiLanguage
                                  key={`answer-${index}-${key}`}
                                  id={`answer-${index}-${key}`}
                                  type="text"
                                  label=""
                                  className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                  topClassName="w-full"
                                  placeholder={`${translateText(
                                    "answer",
                                    language,
                                  )} ${key}`}
                                  required={true}
                                  showLabel={false}
                                  state={createQuestionsFormData}
                                  stateName="question"
                                  valueHandler={getCreateAnswerText}
                                  valueHandlerArgs={[index, key]}
                                  onChangeHandler={createAnswerText}
                                  onChangeHandlerArgs={[index, key]}
                                  defaultLanguage={language}
                                  languageChoices={
                                    surveysData.length === 0
                                      ? ["EN", "FR", "IT", "DE"]
                                      : Object.keys(surveysData[0].name)
                                  }
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  <Button
                    type="button"
                    className={`${getButtonStyle("solid", "primary")}`}
                    onClick={addOrRemoveQuestionFromSurveyCreate}
                    onClickArgs={["add", null]}
                  >
                    <PlusCircleIcon
                      className={`h-5 w-5 text-white mr-1`}
                      aria-hidden="true"
                    />
                    {translateText("add_another_question", language)}
                  </Button>
                </>
              ) : (
                // Scored survey build
                <>
                  {/* Questions sections */}
                  {createQuestionsFormData.map((value: any, index: number) => (
                    <div
                      key={index}
                      className="grid grid-cols-1 gap-y-8 border-t border-light pt-4"
                    >
                      <div>
                        {/* Delete question */}
                        {createQuestionsFormData.length > 1 && (
                          <div className="flex justify-start">
                            <Button
                              type="button"
                              className={`${getButtonStyle(
                                "solid",
                                "danger",
                              )} mb-3`}
                              onClick={addOrRemoveQuestionFromSurveyCreate}
                              onClickArgs={["remove", value.id]}
                            >
                              <TrashIcon
                                className={`h-5 w-5 text-white`}
                                aria-hidden="true"
                              />
                            </Button>
                          </div>
                        )}

                        <div className="col-span-full">
                          <div>
                            {/* Question */}
                            <label
                              htmlFor={`header-${index}`}
                              className={`mb-3 block text-left text-sm font-medium text-black`}
                            >
                              {`${translateText("question", language)} ${
                                index + 1
                              }`}
                            </label>
                            <TextInputMultiLanguage
                              id={`question-${index}`}
                              type="text"
                              label=""
                              className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                              placeholder={`${translateText(
                                "add_question_for",
                                language,
                              )} ${translateText("question", language)} ${
                                index + 1
                              }`}
                              required={true}
                              showLabel={false}
                              state={createQuestionsFormData}
                              stateName="question"
                              valueHandler={getCreateQuestionText}
                              valueHandlerArgs={[index]}
                              onChangeHandler={createQuestionText}
                              onChangeHandlerArgs={[index]}
                              defaultLanguage={language}
                              languageChoices={
                                surveysData.length === 0
                                  ? ["EN", "FR", "IT", "DE"]
                                  : Object.keys(surveysData[0].name)
                              }
                            />

                            {/* Answers */}
                            <div className="mt-4 grid grid-cols-1 sm:grid-cols-1 gap-4">
                              {Object.keys(value.answers).map(
                                (key: any, idx: number) => (
                                  <div
                                    className="inline flex"
                                    key={`answer-${key}-${idx}`}
                                  >
                                    <div className="border border-secondary/40 rounded-md mr-2 flex py-2 px-3">
                                      <input
                                        id={`question-checkbox-${index}-${key}`}
                                        value={value.id}
                                        aria-describedby={`question-checkbox-${index}-${key}`}
                                        name={`question-checkbox-${index}-${key}`}
                                        type="checkbox"
                                        className={`h-4 w-4 rounded border-secondary-300 text-primary focus:ring-primary m-auto`}
                                        onChange={(e) => {
                                          const existingValues = [
                                            ...createQuestionsFormData,
                                          ];
                                          existingValues[index].correct_answer =
                                            idx + 1;
                                          setCreateQuestionsFormData(
                                            existingValues,
                                          );
                                        }}
                                        checked={
                                          createQuestionsFormData[index]
                                            .correct_answer ===
                                          idx + 1
                                        }
                                      />
                                    </div>
                                    <TextInputMultiLanguage
                                      key={`answer-${index}-${key}`}
                                      id={`answer-${index}-${key}`}
                                      type="text"
                                      label=""
                                      className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                      topClassName="w-full"
                                      placeholder={`${translateText(
                                        "answer",
                                        language,
                                      )} ${key}`}
                                      required={true}
                                      showLabel={false}
                                      state={createQuestionsFormData}
                                      stateName="question"
                                      valueHandler={getCreateAnswerText}
                                      valueHandlerArgs={[index, key]}
                                      onChangeHandler={createAnswerText}
                                      onChangeHandlerArgs={[index, key]}
                                      defaultLanguage={language}
                                      languageChoices={
                                        surveysData.length === 0
                                          ? ["EN", "FR", "IT", "DE"]
                                          : Object.keys(surveysData[0].name)
                                      }
                                    />
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  <Button
                    type="button"
                    className={`${getButtonStyle("solid", "primary")}`}
                    onClick={addOrRemoveQuestionFromSurveyCreate}
                    onClickArgs={["add", null]}
                  >
                    <PlusCircleIcon
                      className={`h-5 w-5 text-white mr-1`}
                      aria-hidden="true"
                    />
                    {translateText("add_another_question", language)}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Edit survey modal */}
      <Modal
        show={showEditSurveyModal}
        setShow={setShowEditSurveyModal}
        confirmText={translateText("edit_survey", language)}
        cancelText="Cancel"
        confirmHandler={confirmEditSurvey}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {translateText("edit_survey", language)}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Name */}
              <TextInputMultiLanguage
                id="name"
                type="text"
                label={translateText("name", language)}
                className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={selectedEditSurvey}
                stateName="name"
                onChange={setSelectedEditSurvey}
                defaultLanguage={language}
                languageChoices={
                  surveysData.length === 0
                    ? ["EN", "FR", "IT", "DE"]
                    : Object.keys(surveysData[0].name)
                }
              />

              {/* Status */}
              <SwitchToggle
                id="status"
                label={translateText("status", language)}
                showLabel={true}
                description={translateText("status_survey_subtext", language)}
                toggle={
                  selectedEditSurvey === null
                    ? false
                    : selectedEditSurvey.status === "A"
                }
                state={selectedEditSurvey}
                stateName="status"
                handler={setSelectedEditSurvey}
              />

              {/* Survey type */}
              <Dropdown
                id="type"
                className="col-span-full"
                label={translateText("type", language)}
                showLabel={true}
                required={true}
                state={selectedEditSurvey}
                stateName="type"
                onChange={setSelectedEditSurvey}
                defaultValue={
                  selectedEditSurvey === null ? "M" : selectedEditSurvey.type[0]
                }
              >
                {surveyTypeChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Target */}
              <HorizontalRadio
                label={translateText("target_rooms", language)}
                small={translateText("target_rooms_subtext", language)}
              >
                <>
                  {targetTypes.map((choice) => (
                    <div key={choice[0]} className="flex items-center">
                      <input
                        id={choice[0]}
                        name="build_type"
                        type="radio"
                        defaultChecked={
                          selectedEditSurvey === null
                            ? false
                            : selectedEditSurvey.room_target === choice[0]
                        }
                        className="h-4 w-4 border-gray-300 text-primary focus:ring-primary rounded"
                        value={choice[0]}
                        onChange={(e) => {
                          if (selectedEditSurvey === null) return;
                          setSelectedEditSurvey({
                            ...selectedEditSurvey,
                            room_target: e.target.value,
                          });
                        }}
                      />
                      <label
                        htmlFor={choice[0]}
                        className="ml-3 block text-sm font-medium leading-6 text-black"
                      >
                        {choice[1]}
                      </label>
                    </div>
                  ))}
                </>
              </HorizontalRadio>

              {/* Rooms */}
              {selectedEditSurvey !== null &&
                selectedEditSurvey.room_target === "S" && (
                  <>
                    <div>
                      <label className="mb-1 block text-left text-sm font-medium text-black">
                        {translateText("rooms", language)}
                      </label>
                      <p className="text-left text-xs italic text-secondary">
                        {translateText("rooms_template_subtext", language)}
                      </p>
                    </div>

                    {/* Select/unselect all buttons */}
                    <SelectOrUnselectAll
                      handler={selectOrUnselectAllUpdateRooms}
                      language={language}
                    />
                    <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                      {roomsData.map((room: any) => (
                        <Checkbox
                          key={room.id}
                          id={room.id}
                          onChange={updateRoomForUpdateSurvey}
                          checked={selectedEditSurvey.rooms.includes(room.id)}
                          label={room.name}
                        />
                      ))}
                    </div>
                  </>
                )}

              {/* Unscored survey build */}
              {selectedEditSurvey !== null &&
              selectedEditSurvey.scored === false ? (
                <>
                  {/* Questions sections */}
                  {updateQuestionsFormData.map((value: any, index: number) => (
                    <div
                      key={`question-${value.id}-${index}`}
                      className="grid grid-cols-1 gap-y-8 border-t border-light pt-4"
                    >
                      <div>
                        {/* Delete question */}
                        {updateQuestionsFormData.length > 1 && (
                          <div className="flex justify-start">
                            <Button
                              type="button"
                              className={`${getButtonStyle(
                                "solid",
                                "danger",
                              )} mb-3`}
                              onClick={addOrRemoveQuestionFromSurveyUpdate}
                              onClickArgs={["remove", value.id]}
                            >
                              <TrashIcon
                                className={`h-5 w-5 text-white`}
                                aria-hidden="true"
                              />
                            </Button>
                          </div>
                        )}

                        <div className="col-span-full">
                          <div>
                            {/* Question */}
                            <label
                              htmlFor={`header-${index}`}
                              className={`mb-3 block text-left text-sm font-medium text-black`}
                            >
                              {`${translateText("question", language)} ${
                                index + 1
                              }`}
                            </label>
                            <TextInputMultiLanguage
                              id={`question-${index}`}
                              type="text"
                              label=""
                              className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                              placeholder={`${translateText(
                                "add_question_for",
                                language,
                              )} ${translateText("question", language)} ${
                                index + 1
                              }`}
                              required={true}
                              showLabel={false}
                              state={createQuestionsFormData}
                              stateName="question"
                              valueHandler={getSelectedQuestionText}
                              valueHandlerArgs={[index]}
                              onChangeHandler={updateQuestionText}
                              onChangeHandlerArgs={[index]}
                              defaultLanguage={language}
                              languageChoices={
                                surveysData.length === 0
                                  ? ["EN", "FR", "IT", "DE"]
                                  : Object.keys(surveysData[0].name)
                              }
                            />

                            {/* Answers */}
                            <div className="mt-4 grid grid-cols-1 sm:grid-cols-1 gap-4">
                              {Object.keys(value.answers).map((key: any) => (
                                <TextInputMultiLanguage
                                  key={`answer-${index}-${key}`}
                                  id={`answer-${index}-${key}`}
                                  type="text"
                                  label=""
                                  className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                  topClassName="w-full"
                                  placeholder={`${translateText(
                                    "answer",
                                    language,
                                  )} ${key}`}
                                  required={true}
                                  showLabel={false}
                                  state={createQuestionsFormData}
                                  stateName="question"
                                  valueHandler={getSelectedAnswerText}
                                  valueHandlerArgs={[index, key]}
                                  onChangeHandler={updateAnswerText}
                                  onChangeHandlerArgs={[index, key]}
                                  defaultLanguage={language}
                                  languageChoices={
                                    surveysData.length === 0
                                      ? ["EN", "FR", "IT", "DE"]
                                      : Object.keys(surveysData[0].name)
                                  }
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  <Button
                    type="button"
                    className={`${getButtonStyle("solid", "primary")}`}
                    onClick={addOrRemoveQuestionFromSurveyUpdate}
                    onClickArgs={["add", null]}
                  >
                    <PlusCircleIcon
                      className={`h-5 w-5 text-white mr-1`}
                      aria-hidden="true"
                    />
                    {translateText("add_another_question", language)}
                  </Button>
                </>
              ) : (
                // Scored survey build
                <>
                  {/* Questions sections */}
                  {updateQuestionsFormData.map((value: any, index: number) => (
                    <div
                      key={index}
                      className="grid grid-cols-1 gap-y-8 border-t border-light pt-4"
                    >
                      <div>
                        {/* Delete question */}
                        {updateQuestionsFormData.length > 1 && (
                          <div className="flex justify-start">
                            <Button
                              type="button"
                              className={`${getButtonStyle(
                                "solid",
                                "danger",
                              )} mb-3`}
                              onClick={addOrRemoveQuestionFromSurveyUpdate}
                              onClickArgs={["remove", value.id]}
                            >
                              <TrashIcon
                                className={`h-5 w-5 text-white`}
                                aria-hidden="true"
                              />
                            </Button>
                          </div>
                        )}

                        <div className="col-span-full">
                          <div>
                            {/* Question */}
                            <label
                              htmlFor={`header-${index}`}
                              className={`mb-3 block text-left text-sm font-medium text-black`}
                            >
                              {`${translateText("question", language)} ${
                                index + 1
                              }`}
                            </label>
                            <TextInputMultiLanguage
                              id={`question-${index}`}
                              type="text"
                              label=""
                              className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                              placeholder={`${translateText(
                                "add_question_for",
                                language,
                              )} ${translateText("question", language)} ${
                                index + 1
                              }`}
                              required={true}
                              showLabel={false}
                              state={createQuestionsFormData}
                              stateName="question"
                              valueHandler={getSelectedQuestionText}
                              valueHandlerArgs={[index]}
                              onChangeHandler={updateQuestionText}
                              onChangeHandlerArgs={[index]}
                              defaultLanguage={language}
                              languageChoices={
                                surveysData.length === 0
                                  ? ["EN", "FR", "IT", "DE"]
                                  : Object.keys(surveysData[0].name)
                              }
                            />

                            {/* Answers */}
                            <div className="mt-4 grid grid-cols-1 sm:grid-cols-1 gap-4">
                              {Object.keys(value.answers).map(
                                (key: any, idx: number) => (
                                  <div
                                    className="inline flex"
                                    key={`answer-${key}-${idx}`}
                                  >
                                    <div className="border border-secondary/40 rounded-md mr-2 flex py-2 px-3">
                                      <input
                                        id={`question-checkbox-${index}-${key}`}
                                        value={value.id}
                                        aria-describedby={`question-checkbox-${index}-${key}`}
                                        name={`question-checkbox-${index}-${key}`}
                                        type="checkbox"
                                        className={`h-4 w-4 rounded border-secondary-300 text-primary focus:ring-primary m-auto`}
                                        onChange={(e) => {
                                          const existingValues = [
                                            ...updateQuestionsFormData,
                                          ];
                                          existingValues[index].correct_answer =
                                            idx + 1;
                                          setUpdateQuestionsFormData(
                                            existingValues,
                                          );
                                        }}
                                        checked={
                                          updateQuestionsFormData[index]
                                            .correct_answer ===
                                          idx + 1
                                        }
                                      />
                                    </div>
                                    <TextInputMultiLanguage
                                      key={`answer-${index}-${key}`}
                                      id={`answer-${index}-${key}`}
                                      type="text"
                                      label=""
                                      className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                      topClassName="w-full"
                                      placeholder={`${translateText(
                                        "answer",
                                        language,
                                      )} ${key}`}
                                      required={true}
                                      showLabel={false}
                                      state={createQuestionsFormData}
                                      stateName="question"
                                      valueHandler={getSelectedAnswerText}
                                      valueHandlerArgs={[index, key]}
                                      onChangeHandler={updateAnswerText}
                                      onChangeHandlerArgs={[index, key]}
                                      defaultLanguage={language}
                                      languageChoices={
                                        surveysData.length === 0
                                          ? ["EN", "FR", "IT", "DE"]
                                          : Object.keys(surveysData[0].name)
                                      }
                                    />
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  <Button
                    type="button"
                    className={`${getButtonStyle("solid", "primary")}`}
                    onClick={addOrRemoveQuestionFromSurveyUpdate}
                    onClickArgs={["add", null]}
                  >
                    <PlusCircleIcon
                      className={`h-5 w-5 text-white mr-1`}
                      aria-hidden="true"
                    />
                    {translateText("add_another_question", language)}
                  </Button>
                </>
              )}

              {/* Status */}
              <SwitchToggle
                id="status"
                label={translateText("status", language)}
                description={translateText("status_survey_subtext", language)}
                showLabel={true}
                toggle={
                  selectedEditSurvey === null
                    ? false
                    : selectedEditSurvey.status === "A"
                }
                state={selectedEditSurvey}
                stateName="status"
                handler={setSelectedEditSurvey}
                mapping={{ true: "A", false: "I" }}
              />

              {/* Danger zone */}
              {selectedEditSurvey !== null && (
                <DangerZone
                  objectId={selectedEditSurvey.id}
                  handler={confirmDeleteSurvey}
                  language={language}
                />
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Loading spinner */}
      {surveys.loading ? (
        <SpaceFiller>
          <Spinner />
        </SpaceFiller>
      ) : (
        // Page content
        <SpaceFiller verticalPosition="start">
          <Container className="justify-center mt-10">
            <Filters
              language={language}
              className="pb-10"
              filters={[
                {
                  id: "scored",
                  name: translateText("scored", language),
                  options: scoredOptions,
                },
              ]}
              queryParams={[
                ["dateRange", dateRangeTerm],
                ["scored", searchScored],
              ]}
              handlers={{
                scored: {
                  state: scored,
                  stateName: "value",
                  onChange: setScored,
                },
                dateRange: {
                  state: dateRange,
                  stateName: "N/A",
                  onChange: setDateRange,
                },
              }}
              loading={surveys.loading}
              baseUrl="/surveys"
              showDateRange={true}
              showFilters={true}
              showSearchBar={false}
            />
            {surveysData.length > 0 ? (
              <>
                <div className="mx-1 mb-1 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {surveysData.map((svy) => {
                    return (
                      <div key={svy.id} className="relative">
                        <Card
                          className={`hover:ring-1 hover:ring-secondary/50 hover:ring-inset`}
                        >
                          {/* Card header */}
                          <div className={`border-b border-light pb-2`}>
                            <div className="items-center justify-between">
                              <div className="flex">
                                <h3
                                  className={`flex-grow text-base font-semibold leading-6 text-black truncate`}
                                >
                                  {svy.name[language]}
                                </h3>
                                <div>
                                  <Button
                                    type="button"
                                    className={`group inline-flex justify-center rounded-full text-sm bg-transparent`}
                                    onClick={selectSurveyToEdit}
                                    onClickArgs={[svy.id]}
                                  >
                                    <Cog8ToothIcon
                                      className={`h-6 w-6 text-secondary group-hover:text-black`}
                                      aria-hidden="true"
                                    />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>

                          <a
                            key={svy.id}
                            href="#!"
                            onClick={(e) =>
                              getSelectedSurveyResponses(e, svy.id)
                            }
                          >
                            <div className="pt-2 grid grid-cols-1 space-y-2">
                              <div className="inline-flex text-sm text-secondary">
                                <QuestionMarkCircleIcon
                                  className="h-5 w-5 text-info mr-1"
                                  aria-hidden="true"
                                />
                                {`${svy.questions.length} ${
                                  svy.questions.length === 1
                                    ? translateText("question", language, true)
                                    : translateText("questions", language, true)
                                }`}
                              </div>
                              <div className="inline-flex text-sm text-secondary">
                                {svy.scored ? (
                                  <>
                                    <ClipboardDocumentCheckIcon
                                      className="h-5 w-5 text-info mr-1"
                                      aria-hidden="true"
                                    />
                                    {translateText("scored", language)}
                                  </>
                                ) : (
                                  <>
                                    <ClipboardDocumentListIcon
                                      className="h-5 w-5 text-info mr-1"
                                      aria-hidden="true"
                                    />
                                    {translateText("not_scored", language)}
                                  </>
                                )}
                              </div>
                              <div className="inline-flex text-sm text-secondary">
                                <ClockIcon
                                  className="h-5 w-5 text-secondary mr-1"
                                  aria-hidden="true"
                                />
                                {svy.datetime_created}
                              </div>
                            </div>
                          </a>
                        </Card>

                        {/* Status indicator */}
                        <Indicator
                          size={3}
                          colour={svy.status[0] === "A" ? "success" : "warning"}
                          pulse={false}
                        />
                      </div>
                    );
                  })}
                </div>

                {/* Survey response analytics */}
                <div id="survey-response-anchor" />
                {selectedSurvey !== null && (
                  <div className="mt-16">
                    {surveys.responses.loading ? (
                      <SpaceFiller>
                        <Spinner className="mx-auto" />
                      </SpaceFiller>
                    ) : selectedSurveyResponses.length === 0 ? (
                      <NoContent
                        text={translateText("no_survey_responses", language)}
                        subtext={translateText(
                          "no_survey_responses_subtext",
                          language,
                        )}
                        icon={
                          <ExclamationCircleIcon
                            className="mx-auto h-12 w-12 text-secondary animate-bounce"
                            aria-hidden="true"
                          />
                        }
                      />
                    ) : (
                      <div className="space-y-2">
                        <div className="row flex justify-end">
                          <Button
                            type="button"
                            className={`rounded-full p-2 bg-transparent text-black hover:bg-white/50`}
                            onClick={resetSelectedSurvey}
                          >
                            <XMarkIcon
                              className="h-5 w-5 text-black"
                              aria-hidden="true"
                            />
                          </Button>
                        </div>

                        {activeSurveyData !== null &&
                        activeSurveyData.scored ? (
                          <div className="grid grid-cols-1 gap-y-4 px-1">
                            {/* Response charts */}
                            <div className="grid md:grid-cols-3 grid-cols-1 mt-6 gap-4">
                              {/* Answer frequency */}
                              {answerFrequencyData.length > 0 &&
                                answerFrequencyData.map(
                                  (x: any, idx: number) => (
                                    <Card
                                      className="overflow-x-auto scrollbar-invisible"
                                      key={`chart-${idx}`}
                                    >
                                      <div className="flex w-full items-center justify-between space-x-6">
                                        <div className="flex-1">
                                          <div className="border-b border-gray-200 px-4 pb-4 pt-0 mb-4 sm:px-6">
                                            <h3 className="text-base font-semibold leading-6 text-black">
                                              {x.question}
                                            </h3>
                                            <span className="text-xs text-secondary">{`${
                                              x.correct_pct
                                            }% ${translateText(
                                              "answered_correctly",
                                              language,
                                              true,
                                            )}`}</span>
                                          </div>
                                          <DonutChart
                                            data={x.answers}
                                            className="mx-auto text-sm lg:h-64 h-44"
                                          />
                                        </div>
                                      </div>
                                    </Card>
                                  ),
                                )}

                              {/* Room frequency */}
                              {roomFrequency.length > 0 && (
                                <Card className="overflow-x-auto scrollbar-invisible">
                                  <div className="flex w-full items-center justify-between space-x-6">
                                    <div className="flex-1">
                                      <div className="border-b border-gray-200 px-4 pb-4 pt-0 mb-4 sm:px-6">
                                        <h3 className="text-base font-semibold leading-6 text-black">
                                          {translateText(
                                            "room_frequency",
                                            language,
                                          )}
                                        </h3>
                                        <span className="text-xs text-secondary">
                                          {translateText(
                                            "room_frequency_survey",
                                            language,
                                          )}
                                        </span>
                                      </div>
                                      <DonutChart
                                        data={roomFrequency}
                                        className="mx-auto text-sm lg:h-64 h-44"
                                      />
                                    </div>
                                  </div>
                                </Card>
                              )}
                            </div>

                            {/* Response table */}
                            <Table
                              className="mb-1"
                              header={
                                <tr>
                                  <th className="px-2 py-3">
                                    {translateText("timestamp", language)}
                                  </th>
                                  <th className="px-2 py-3">
                                    {translateText("question", language)}
                                  </th>
                                  <th className="px-2 py-3">
                                    {translateText("room", language)}
                                  </th>
                                  <th className="px-2 py-3">
                                    {translateText("answer", language)}
                                  </th>
                                  <th className="px-2 py-3">
                                    {translateText("correct", language)}
                                  </th>
                                </tr>
                              }
                              body={tableRowData.map((response) => (
                                <tr
                                  key={response.id}
                                  className="hover:bg-light/20"
                                >
                                  <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                                    {response.datetime}
                                  </td>
                                  <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                                    {response.question}
                                  </td>
                                  <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                                    {response.room}
                                  </td>
                                  <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                                    {response.answer}
                                  </td>
                                  <td className="px-2 py-3">
                                    <Badge
                                      text={response.correct[1]}
                                      bgColour={getColour(response.correct[0])}
                                      textColour={`${getColour(
                                        response.correct[0],
                                      )}-dark`}
                                      ringColour={`${getColour(
                                        response.correct[0],
                                      )}-dark`}
                                    />
                                  </td>
                                </tr>
                              ))}
                              showPagination={false}
                              paginationHasPrevious={false}
                              paginationHasNext={false}
                              paginationCurrentPage={1}
                              paginationTotalPages={1}
                              paginationTotalObjects={tableRowData.length}
                              paginationQueryParams={[]}
                            />
                          </div>
                        ) : (
                          activeSurveyData !== null &&
                          !activeSurveyData.scored && (
                            <div className="grid grid-cols-1 gap-y-4 px-1">
                              {/* Response charts */}
                              <div className="grid md:grid-cols-3 grid-cols-1 mt-6 gap-4">
                                {/* Answer frequency */}
                                {answerFrequencyData.length > 0 &&
                                  answerFrequencyData.map(
                                    (x: any, idx: number) => (
                                      <Card
                                        className="overflow-x-auto scrollbar-invisible"
                                        key={`chart-${idx}`}
                                      >
                                        <div className="flex w-full items-center justify-between space-x-6">
                                          <div className="flex-1">
                                            <div className="border-b border-gray-200 px-4 pb-4 pt-0 mb-4 sm:px-6">
                                              <h3 className="text-base font-semibold leading-6 text-black">
                                                {x.question}
                                              </h3>
                                            </div>
                                            <DonutChart
                                              data={x.answers}
                                              className="mx-auto text-sm lg:h-64 h-44"
                                            />
                                          </div>
                                        </div>
                                      </Card>
                                    ),
                                  )}

                                {/* Room frequency */}
                                {roomFrequency.length > 0 && (
                                  <Card className="overflow-x-auto scrollbar-invisible">
                                    <div className="flex w-full items-center justify-between space-x-6">
                                      <div className="flex-1">
                                        <div className="border-b border-gray-200 px-4 pb-4 pt-0 mb-4 sm:px-6">
                                          <h3 className="text-base font-semibold leading-6 text-black">
                                            {translateText(
                                              "room_frequency",
                                              language,
                                            )}
                                          </h3>
                                          <span className="text-xs text-secondary">
                                            {translateText(
                                              "room_frequency_survey",
                                              language,
                                            )}
                                          </span>
                                        </div>
                                        <DonutChart
                                          data={roomFrequency}
                                          className="mx-auto text-sm lg:h-64 h-44"
                                        />
                                      </div>
                                    </div>
                                  </Card>
                                )}
                              </div>

                              {/* Response table */}
                              <Table
                                className="mb-1"
                                header={
                                  <tr>
                                    <th className="px-2 py-3">
                                      {translateText("timestamp", language)}
                                    </th>
                                    <th className="px-2 py-3">
                                      {translateText("question", language)}
                                    </th>
                                    <th className="px-2 py-3">
                                      {translateText("room", language)}
                                    </th>
                                    <th className="px-2 py-3">
                                      {translateText("answer", language)}
                                    </th>
                                  </tr>
                                }
                                body={tableRowData.map((response) => (
                                  <tr
                                    key={response.id}
                                    className="hover:bg-light/20"
                                  >
                                    <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                                      {response.datetime}
                                    </td>
                                    <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                                      {response.question}
                                    </td>
                                    <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                                      {response.room}
                                    </td>
                                    <td className="px-2 py-3 whitespace-nowrap">
                                      {response.answer}
                                    </td>
                                  </tr>
                                ))}
                                showPagination={false}
                                paginationHasPrevious={false}
                                paginationHasNext={false}
                                paginationCurrentPage={1}
                                paginationTotalPages={1}
                                paginationTotalObjects={tableRowData.length}
                                paginationQueryParams={[]}
                              />
                            </div>
                          )
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              // No content placeholder
              <NoContent
                text={translateText("no_surveys", language)}
                subtext={translateText("no_surveys_subtext", language)}
                icon={
                  <ExclamationCircleIcon
                    className="mx-auto h-12 w-12 text-secondary animate-bounce"
                    aria-hidden="true"
                  />
                }
              />
            )}
          </Container>
        </SpaceFiller>
      )}
    </Template>
  );
}
